import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTools,
  faSearch,
  faDatabase,
  faGlobe,
  faSatellite,
  faFileCode,
  faCloud,
  faCogs,
  faLightbulb,
  faHammer,
  faCode,
  faTags,
  faChevronRight,
  faChevronDown,
  faExclamationTriangle,
  faTimes,
  faInfoCircle,
  faRobot,
  faExternalLinkAlt,
  faFilter,
  faClock,
  faRefresh,
  faDownload,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';

const DebuggingAssistant = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [liveLogs, setLiveLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [toolFilter, setToolFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('live'); // 'live' or 'knowledge'

  // EO Mini-Playbooks
  const eoPlaybooks = [
    {
      id: 'pb1',
      name: 'WMS GetMap Timeout (Large Extent)',
      symptoms: ['GeoServer timeout', 'Large extent query', 'Slow layer rendering'],
      actions: [
        '1. Build overviews: gdaladdo -r average input.tif 2 4 8 16 32',
        '2. Convert to COG: gdal_translate -of COG -co COMPRESS=LZW input.tif output.tif',
        '3. Enable JAI tile cache in GeoServer: JAVA_OPTS="-Dorg.geotools.coverage.jaiext.enabled=true"',
        '4. Use ImageMosaic with USE_IMAGEREAD=true',
        '5. Enable GeoWebCache seeding for common zoom levels'
      ]
    },
    {
      id: 'pb2',
      name: 'Xarray/Dask Out of Memory',
      symptoms: ['MemoryError', 'Worker nodes exhausted', 'Memory pressure'],
      actions: [
        '1. Rechunk to 2D tiles: ds = xr.open_dataset("file.nc", chunks={"x":1024,"y":1024,"band":1})',
        '2. Enable spill to disk: dask.config.set({"distributed.worker.memory.spill": 0.85})',
        '3. Limit workers: dask-worker --nthreads 4 --memory-limit 4GB',
        '4. Persist intermediate results: result = computation.persist()',
        '5. Use lazy evaluation: avoid .values or .compute() until necessary'
      ]
    },
    {
      id: 'pb3',
      name: 'PostGIS Slow ST_Intersects Query',
      symptoms: ['Slow spatial query', 'High CPU on PostGIS', 'Query timeout'],
      actions: [
        '1. Add GIST spatial index: CREATE INDEX idx_geom ON table USING GIST(geom);',
        '2. Run ANALYZE: ANALYZE table;',
        '3. Use bbox filter first: WHERE geom && ST_MakeEnvelope(...) AND ST_Intersects(...)',
        '4. Subdivide large geometries: UPDATE table SET geom = ST_Subdivide(geom, 256);',
        '5. Check index bloat: SELECT * FROM pgstattuple(\'idx_geom\');'
      ]
    },
    {
      id: 'pb4',
      name: 'STAC Validation Failures',
      symptoms: ['Invalid STAC item', 'Missing required fields', 'Wrong proj:* metadata'],
      actions: [
        '1. Run stac-validator: stac-validator item.json --core --extensions',
        '2. Autofill proj:* fields from raster: rio info --proj item.tif',
        '3. Compute accurate bbox: ogr2ogr -f GeoJSON -t_srs EPSG:4326 bbox.json item.tif',
        '4. Add eo:cloud_cover from QA band: gdal_calc.py --calc "A>0" --outfile cloud_mask.tif',
        '5. Validate datetime format: ensure ISO 8601 (YYYY-MM-DDTHH:MM:SSZ)'
      ]
    }
  ];

  // Mock data for common EO error messages and solutions
  const errorDatabase = [
    {
      id: 1,
      category: 'GDAL',
      errorMessage: 'ERROR 4: Unable to open EPSG support file gcs.csv',
      explanation: 'GDAL cannot find the EPSG coordinate system definition files. This usually happens when GDAL_DATA environment variable is not set correctly.',
      solution: 'Set the GDAL_DATA environment variable to point to your GDAL data directory containing the EPSG files.',
      codeExample: 'export GDAL_DATA=/usr/share/gdal\n# or on Windows:\n# set GDAL_DATA=C:\\OSGeo4W64\\share\\gdal',
      keywords: ['epsg', 'coordinate system', 'projection', 'gcs.csv', 'gdal_data']
    },
    {
      id: 2,
      category: 'GDAL',
      errorMessage: 'ERROR 1: PROJ: proj_create_from_database: Cannot find proj.db',
      explanation: 'PROJ library cannot find its database file. This is required for coordinate transformations and projections.',
      solution: 'Set the PROJ_LIB environment variable or ensure PROJ database is installed correctly.',
      codeExample: 'export PROJ_LIB=/usr/share/proj\n# Check if proj.db exists:\n# ls -la /usr/share/proj/proj.db',
      keywords: ['proj', 'projection', 'transformation', 'proj.db', 'proj_lib']
    },
    {
      id: 3,
      category: 'GeoServer',
      errorMessage: 'java.lang.OutOfMemoryError: Java heap space',
      explanation: 'GeoServer has run out of memory while processing requests. This commonly happens with large datasets or complex WMS requests.',
      solution: 'Increase JVM heap size and optimize GeoServer configuration for large datasets.',
      codeExample: 'export JAVA_OPTS="-Xmx4g -Xms1g"\n# Add to startup script:\n# -Dorg.geotools.coverage.jaiext.enabled=true',
      keywords: ['memory', 'heap', 'java', 'outofmemory', 'performance']
    },
    {
      id: 4,
      category: 'PostGIS',
      errorMessage: 'ERROR: geometry contains non-closed rings',
      explanation: 'A polygon geometry has rings that are not properly closed. This violates the Simple Features specification.',
      solution: 'Use ST_MakeValid() to fix invalid geometries or check your data import process.',
      codeExample: 'UPDATE your_table SET geom = ST_MakeValid(geom) WHERE NOT ST_IsValid(geom);\n-- Or check validity:\n-- SELECT ST_IsValidReason(geom) FROM your_table;',
      keywords: ['geometry', 'polygon', 'invalid', 'rings', 'st_makevalid']
    },
    {
      id: 5,
      category: 'SNAP',
      errorMessage: 'java.awt.HeadlessException: No X11 DISPLAY variable was set',
      explanation: 'SNAP is trying to create a GUI component on a headless server without a display.',
      solution: 'Set the java.awt.headless system property to true for server environments.',
      codeExample: 'export _JAVA_OPTIONS="-Djava.awt.headless=true"\n# Or in your Java command:\n# java -Djava.awt.headless=true -jar your-snap-app.jar',
      keywords: ['headless', 'display', 'x11', 'gui', 'server']
    },
    {
      id: 6,
      category: 'Rasterio',
      errorMessage: 'rasterio.errors.CRSError: The WKT could not be parsed',
      explanation: 'The coordinate reference system (CRS) information in the raster file is malformed or not recognized.',
      solution: 'Manually set the CRS or fix the WKT string in the raster file.',
      codeExample: 'import rasterio\nfrom rasterio.crs import CRS\n\n# Set CRS manually\nwith rasterio.open("file.tif", "r+") as src:\n    src.crs = CRS.from_epsg(4326)',
      keywords: ['crs', 'wkt', 'projection', 'coordinate system', 'epsg']
    },
    {
      id: 7,
      category: 'NetCDF',
      errorMessage: 'AttributeError: NetCDF: HDF error',
      explanation: 'The NetCDF file is corrupted or has HDF5 format issues. This can happen with incomplete downloads or disk errors.',
      solution: 'Verify file integrity and re-download if necessary. Check disk space and permissions.',
      codeExample: '# Check file integrity:\n# ncdump -h your_file.nc\n# Or with Python:\nimport netCDF4\ntry:\n    ds = netCDF4.Dataset("file.nc")\n    print("File is valid")\nexcept:\n    print("File is corrupted")',
      keywords: ['netcdf', 'hdf5', 'corrupted', 'file integrity', 'download']
    },
    {
      id: 8,
      category: 'Orfeo Toolbox',
      errorMessage: 'itk::ExceptionObject: Could not create IO object for reading file',
      explanation: 'OTB cannot find an appropriate reader for the input file format. The file might be corrupted or in an unsupported format.',
      solution: 'Check file format compatibility and ensure the file is not corrupted.',
      codeExample: '# Check supported formats:\n# otbcli_ReadImageInfo -in input.tif\n# Or list all supported formats:\n# otbcli_BandMath -help',
      keywords: ['otb', 'file format', 'io', 'reader', 'unsupported']
    },
    {
      id: 9,
      category: 'Docker',
      errorMessage: 'Container exited with code 137 (OOM Killed)',
      explanation: 'The container was killed by the OS because it exceeded available memory limits. This is common with large satellite data processing.',
      solution: 'Increase memory limits in Docker configuration and implement memory-aware processing chunks.',
      codeExample: '# Increase Docker memory limits:\ndocker run --memory=4g --memory-swap=8g your-image\n# Or in docker-compose.yml:\nservices:\n  app:\n    deploy:\n      resources:\n        limits:\n          memory: 4G',
      keywords: ['docker', 'memory', 'oom', 'container', 'limits']
    },
    {
      id: 10,
      category: 'Python',
      errorMessage: 'MemoryError: Unable to allocate array with shape',
      explanation: 'Python cannot allocate enough memory for a large array. This often happens when processing high-resolution satellite imagery.',
      solution: 'Process data in chunks, use memory mapping, or optimize data types.',
      codeExample: 'import numpy as np\nfrom rasterio.windows import Window\n\n# Process in chunks\nfor i in range(0, height, chunk_size):\n    for j in range(0, width, chunk_size):\n        window = Window(j, i, chunk_size, chunk_size)\n        chunk = src.read(1, window=window)',
      keywords: ['python', 'memory', 'array', 'numpy', 'chunks']
    },
    {
      id: 11,
      category: 'GRASS GIS',
      errorMessage: 'ERROR: Vector map is outside current region',
      explanation: 'The vector data extends beyond the computational region boundaries set in GRASS GIS.',
      solution: 'Adjust the computational region to match your data extent using g.region.',
      codeExample: '# Set region from vector map\ng.region vector=your_vector -p\n# Or set region with specific bounds\ng.region n=north s=south e=east w=west res=resolution\n# Align region to resolution\ng.region vector=your_vector res=30 -a',
      keywords: ['grass', 'region', 'vector', 'extent', 'boundaries']
    },
    {
      id: 12,
      category: 'QGIS',
      errorMessage: 'Plugin error: No module named qgis.core',
      explanation: 'QGIS Python environment is not properly configured or QGIS modules are not accessible.',
      solution: 'Ensure QGIS Python environment is properly set up and PYTHONPATH includes QGIS modules.',
      codeExample: '# Set QGIS environment variables\nexport QGIS_PREFIX_PATH=/usr\nexport PYTHONPATH="${PYTHONPATH}:/usr/share/qgis/python"\n# Or programmatically:\nfrom qgis.core import QgsApplication\nQgsApplication.setPrefixPath("/usr", True)',
      keywords: ['qgis', 'python', 'plugin', 'environment', 'pythonpath']
    },
    {
      id: 13,
      category: 'Kubernetes',
      errorMessage: 'Pod has insufficient cpu/memory',
      explanation: 'The Kubernetes pod cannot be scheduled because there are insufficient resources on the cluster.',
      solution: 'Adjust resource requests/limits or scale the cluster to have more available resources.',
      codeExample: '# Update resource requirements\napiVersion: v1\nkind: Pod\nspec:\n  containers:\n  - name: eo-processor\n    resources:\n      requests:\n        memory: "1Gi"\n        cpu: "500m"\n      limits:\n        memory: "2Gi"\n        cpu: "1"',
      keywords: ['kubernetes', 'pod', 'resources', 'cpu', 'memory', 'scheduling']
    },
    {
      id: 14,
      category: 'Elasticsearch',
      errorMessage: 'ElasticsearchException: failed to parse date field',
      explanation: 'Date parsing failed because the date format in your satellite metadata does not match the expected format.',
      solution: 'Define custom date formats in your index mapping or standardize date formats before indexing.',
      codeExample: '{\n  "mappings": {\n    "properties": {\n      "acquisition_date": {\n        "type": "date",\n        "format": "yyyy-MM-dd||yyyy-MM-dd HH:mm:ss||epoch_millis"\n      }\n    }\n  }\n}',
      keywords: ['elasticsearch', 'date', 'parsing', 'mapping', 'format', 'timestamp']
    },
    {
      id: 15,
      category: 'Apache Spark',
      errorMessage: 'java.lang.OutOfMemoryError: GC overhead limit exceeded',
      explanation: 'Spark is spending too much time in garbage collection. This often happens with large geospatial datasets.',
      solution: 'Tune Spark memory settings, increase executor memory, or optimize data partitioning.',
      codeExample: '# Spark configuration\nspark.executor.memory=4g\nspark.executor.memoryFraction=0.8\nspark.sql.adaptive.enabled=true\nspark.sql.adaptive.coalescePartitions.enabled=true\n\n# In your Spark job:\ndf = df.repartition(200)  # Adjust partitioning',
      keywords: ['spark', 'memory', 'gc', 'partitioning', 'executor', 'garbage collection']
    }
  ];

  // Load live logs from API
  useEffect(() => {
    const loadLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const logsData = await apiService.getLogs();

        // Enhance logs with error frequency and fix suggestions
        const enhancedLogs = logsData.map(log => ({
          ...log,
          frequency: Math.floor(Math.random() * 20) + 1, // Mock frequency in last 24h
          lastOccurred: Math.floor(Math.random() * 60) + 1, // Minutes ago
          fixCommand: getFixCommand(log.message),
          docsLink: getDocsLink(log.tool || log.service)
        }));

        setLiveLogs(enhancedLogs);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load logs:', err);
        setError('Failed to load debugging data from API');
        setLoading(false);
      }
    };

    if (activeTab === 'live') {
      loadLogs();
    } else {
      setLoading(false);
    }
  }, [activeTab]);

  const getFixCommand = (message) => {
    if (message.includes('EPSG')) return 'export GDAL_DATA=/usr/share/gdal && gdal-config --datadir';
    if (message.includes('memory')) return 'export GDAL_CACHEMAX=2048 && ulimit -v unlimited';
    if (message.includes('COG')) return 'gdal_translate -of COG -co COMPRESS=LZW input.tif output.tif';
    if (message.includes('PostGIS')) return 'SELECT PostGIS_Version(); VACUUM ANALYZE;';
    if (message.includes('timeout')) return 'nginx -s reload && systemctl restart geoserver';
    return 'systemctl status service_name && journalctl -u service_name';
  };

  const getDocsLink = (tool) => {
    const links = {
      'GDAL': 'https://gdal.org/troubleshooting.html',
      'GeoServer': 'https://docs.geoserver.org/stable/en/user/troubleshooting/',
      'PostGIS': 'https://postgis.net/documentation/',
      'QGIS': 'https://docs.qgis.org/3.28/en/docs/user_manual/troubleshooting/',
      'SAGA-GIS': 'http://www.saga-gis.org/saga_tool_doc/'
    };
    return links[tool] || '#';
  };

  const getSeverityIcon = (severity) => {
    const level = (severity || '').toLowerCase();
    if (level === 'error') return faTimes;
    if (level === 'warning') return faExclamationTriangle;
    return faInfoCircle;
  };

  const getSeverityColor = (severity) => {
    const level = (severity || '').toLowerCase();
    if (level === 'error') return '#ef4444'; // red
    if (level === 'warning') return '#f97316'; // orange
    return '#22c55e'; // green
  };

  const groupLogsByTool = () => {
    const filtered = liveLogs.filter(log => {
      const matchesSearch = searchTerm === '' ||
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (log.tool || log.service || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSeverity = severityFilter === 'all' ||
        (log.level || log.severity)?.toLowerCase() === severityFilter.toLowerCase();

      const matchesTool = toolFilter === 'all' ||
        (log.tool || log.service) === toolFilter;

      return matchesSearch && matchesSeverity && matchesTool;
    });

    return filtered.reduce((groups, log) => {
      const tool = log.tool || log.service || 'Other';
      if (!groups[tool]) {
        groups[tool] = [];
      }
      groups[tool].push(log);
      return groups;
    }, {});
  };

  const toggleGroup = (tool) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(tool)) {
      newExpanded.delete(tool);
    } else {
      newExpanded.add(tool);

      // Auto-expand the first log item in this group
      const toolLogs = liveLogs.filter(log => (log.tool || log.service || 'Other') === tool);
      if (toolLogs.length > 0) {
        const newExpandedItems = new Set(expandedItems);
        newExpandedItems.add(toolLogs[0].id);
        setExpandedItems(newExpandedItems);
      }
    }
    setExpandedGroups(newExpanded);
  };

  const getUniqueTools = () => {
    const tools = [...new Set(liveLogs.map(log => log.tool || log.service || 'Other'))];
    return tools.sort();
  };

  const exportLogs = () => {
    const dataToExport = activeTab === 'live' ? groupLogsByTool() : errorDatabase;
    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `debugging-${activeTab}-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const filteredErrors = errorDatabase.filter(error => {
    const searchLower = searchTerm.toLowerCase();
    return (
      error.errorMessage.toLowerCase().includes(searchLower) ||
      error.category.toLowerCase().includes(searchLower) ||
      error.explanation.toLowerCase().includes(searchLower) ||
      error.keywords.some(keyword => keyword.includes(searchLower))
    );
  });

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'GDAL': faDatabase,
      'GeoServer': faGlobe,
      'PostGIS': faDatabase,
      'SNAP': faSatellite,
      'Rasterio': faFileCode,
      'NetCDF': faCloud,
      'Orfeo Toolbox': faSatellite,
      'Docker': faCogs,
      'Python': faCode,
      'GRASS GIS': faDatabase,
      'QGIS': faGlobe,
      'Kubernetes': faCloud,
      'Elasticsearch': faSearch,
      'Apache Spark': faDatabase
    };
    return icons[category] || faCogs;
  };

  // Loading state
  if (loading && activeTab === 'live') {
    return (
      <div className="debugging-assistant-container">
        <div className="debugging-header">
          <h2>
            <FontAwesomeIcon icon={faTools} style={{ marginRight: '0.5rem' }} />
            Debugging Assistant
          </h2>
        </div>
        <div className="modern-loading-container">
          <div className="modern-loading">
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
            <h3>Loading Error Analysis</h3>
            <p>Analyzing system logs and generating AI suggestions...</p>
            <div className="loading-progress">
              <div className="progress-bar"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && activeTab === 'live') {
    return (
      <div className="debugging-assistant-container">
        <div className="debugging-header">
          <h2>
            <FontAwesomeIcon icon={faTools} style={{ marginRight: '0.5rem' }} />
            Debugging Assistant
          </h2>
        </div>
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.5rem' }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const groupedLogs = groupLogsByTool();

  return (
    <div className="debugging-assistant-container">
      <div className="debugging-header">
        <div className="header-title">
          <h2>
            <FontAwesomeIcon icon={faTools} style={{ marginRight: '0.5rem' }} />
            Debugging Assistant
          </h2>
          <p className="subtitle">Error analysis and resolution guidance for EO processing tools</p>
        </div>
        <div className="header-actions">
          <button className="export-btn" onClick={exportLogs}>
            <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.5rem' }} />
            Export {activeTab === 'live' ? 'Logs' : 'Knowledge'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="debugging-tabs">
        <button
          className={`tab-btn ${activeTab === 'live' ? 'active' : ''}`}
          onClick={() => setActiveTab('live')}
        >
          <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.5rem' }} />
          Live Logs ({liveLogs.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'knowledge' ? 'active' : ''}`}
          onClick={() => setActiveTab('knowledge')}
        >
          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem' }} />
          Knowledge Base ({errorDatabase.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'playbooks' ? 'active' : ''}`}
          onClick={() => setActiveTab('playbooks')}
        >
          <FontAwesomeIcon icon={faHammer} style={{ marginRight: '0.5rem' }} />
          EO Playbooks ({eoPlaybooks.length})
        </button>
      </div>

      {/* Search and Filters */}
      <div className="debugging-filters">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder={activeTab === 'live' ? "Search logs, tools, or errors..." : "Search error messages, tools, or keywords..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {activeTab === 'live' && (
          <div className="filter-controls">
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Severities</option>
              <option value="error">Errors Only</option>
              <option value="warning">Warnings Only</option>
              <option value="info">Info Only</option>
            </select>

            <select
              value={toolFilter}
              onChange={(e) => setToolFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Tools</option>
              {getUniqueTools().map(tool => (
                <option key={tool} value={tool}>{tool}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'playbooks' ? (
        /* EO Playbooks Tab */
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">EO Processing Playbooks ({eoPlaybooks.length} recipes)</h3>
            <span className="table-subtitle">Common scenarios with step-by-step solutions</span>
          </div>
          <div className="table-content">
            <div className="error-list">
              {eoPlaybooks.map(playbook => (
                <div key={playbook.id} className="error-entry expanded">
                  <div className="error-summary">
                    <div className="error-header">
                      <div className="error-category">
                        <FontAwesomeIcon icon={faHammer} className="category-icon" style={{ color: '#3b82f6' }} />
                        <span className="category-name" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{playbook.name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="error-details" style={{ display: 'block' }}>
                    <div className="explanation-section">
                      <h4>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
                        Symptoms
                      </h4>
                      <ul style={{ marginLeft: '1.5rem' }}>
                        {playbook.symptoms.map((symptom, idx) => (
                          <li key={idx}>{symptom}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="solution-section">
                      <h4>
                        <FontAwesomeIcon icon={faHammer} style={{ marginRight: '0.5rem', color: '#10b981' }} />
                        Step-by-Step Actions
                      </h4>
                      <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', padding: '1rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                        {playbook.actions.map((action, idx) => (
                          <div key={idx} style={{ marginBottom: '0.75rem', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {action}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : activeTab === 'live' ? (
        /* Live Logs Tab - Contextual Grouping */
        <div className="debugging-groups">
          {Object.entries(groupedLogs).map(([tool, toolLogs]) => (
            <div key={tool} className="tool-group">
              <div
                className="tool-group-header"
                onClick={() => toggleGroup(tool)}
              >
                <FontAwesomeIcon
                  icon={expandedGroups.has(tool) ? faChevronDown : faChevronRight}
                  className="group-toggle-icon"
                />
                <div className="tool-info">
                  <FontAwesomeIcon icon={getCategoryIcon(tool)} className="tool-icon" />
                  <span className="tool-name">{tool}</span>
                </div>
                <div className="tool-stats">
                  <span className="error-count" style={{ color: '#ef4444' }}>
                    {toolLogs.filter(log => (log.level || log.severity)?.toLowerCase() === 'error').length} errors
                  </span>
                  <span className="warning-count" style={{ color: '#f97316' }}>
                    {toolLogs.filter(log => (log.level || log.severity)?.toLowerCase() === 'warning').length} warnings
                  </span>
                  <span className="total-count">{toolLogs.length} total</span>
                </div>
              </div>

              {expandedGroups.has(tool) && (
                <div className="tool-logs" style={{ marginTop: '1rem' }}>
                  {toolLogs.map(log => (
                    <div key={log.id} className="log-entry" style={{ marginBottom: '1rem' }}>
                      <div
                        className="log-header"
                        onClick={() => toggleExpanded(log.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="log-severity">
                          <FontAwesomeIcon
                            icon={getSeverityIcon(log.level || log.severity)}
                            style={{ color: getSeverityColor(log.level || log.severity) }}
                          />
                          <span className="severity-text">
                            {(log.level || log.severity)?.toUpperCase() || 'INFO'}
                          </span>
                        </div>

                        <div className="log-info">
                          <span className="log-message">{log.message}</span>
                          <div className="log-metadata">
                            <span className="frequency">
                              <FontAwesomeIcon icon={faClock} />
                              {log.frequency}x in 24h
                            </span>
                            <span className="timestamp">
                              {log.lastOccurred}min ago
                            </span>
                          </div>
                        </div>

                        <FontAwesomeIcon
                          icon={expandedItems.has(log.id) ? faChevronDown : faChevronRight}
                          className="log-toggle-icon"
                        />
                      </div>

                      {expandedItems.has(log.id) && (
                        <div className="log-details" style={{ marginTop: '1rem', paddingLeft: '1rem', borderLeft: '3px solid rgba(59, 130, 246, 0.3)' }}>
                          {/* AI Suggestion Panel - Always Visible */}
                          <div className="ai-suggestion-panel">
                            <div className="ai-suggestion-header">
                              <FontAwesomeIcon icon={faRobot} />
                              <strong>What this means</strong>
                            </div>
                            <div className="ai-suggestion-content">
                              <p>{log.expandedContent?.aiSuggestion || 'Analyzing error pattern...'}</p>
                            </div>

                            <div className="fix-section">
                              <h4>Quick Fix</h4>
                              <div className="fix-command">
                                <code>{log.fixCommand}</code>
                                <button
                                  className="copy-btn"
                                  onClick={() => navigator.clipboard.writeText(log.fixCommand)}
                                  title="Copy command"
                                >
                                  <FontAwesomeIcon icon={faCopy} />
                                </button>
                              </div>
                              <a
                                href={log.docsLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="docs-link"
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                Documentation
                              </a>
                            </div>
                          </div>

                          {/* Collapsible Code Example */}
                          {log.expandedContent?.codeSnippet && (
                            <details className="code-section">
                              <summary>
                                <FontAwesomeIcon icon={faCode} />
                                Code Context
                              </summary>
                              <pre className="code-snippet">
                                {log.expandedContent.codeSnippet}
                              </pre>
                            </details>
                          )}

                          {/* Stack Trace if available */}
                          {log.expandedContent?.stackTrace && (
                            <details className="stacktrace-section">
                              <summary>Stack Trace</summary>
                              <pre className="stacktrace">
                                {log.expandedContent.stackTrace}
                              </pre>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {Object.keys(groupedLogs).length === 0 && (
            <div className="no-results">
              <FontAwesomeIcon icon={faSearch} />
              <h3>No logs match your filters</h3>
              <p>Try adjusting your search terms or filters</p>
            </div>
          )}
        </div>
      ) : (
        /* Knowledge Base Tab */
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">EO Processing Error Knowledge Base ({filteredErrors.length} entries)</h3>
            <span className="table-subtitle">Click entries to expand solutions and code examples</span>
          </div>
          <div className="table-content">
            <div className="error-list">
              {filteredErrors.map(error => (
                <div
                  key={error.id}
                  className={`error-entry ${expandedItems.has(error.id) ? 'expanded' : ''}`}
                >
                  <div
                    className="error-summary"
                    onClick={() => toggleExpanded(error.id)}
                  >
                    <div className="error-header">
                      <div className="error-category">
                        <FontAwesomeIcon icon={getCategoryIcon(error.category)} className="category-icon" />
                        <span className="category-name">{error.category}</span>
                      </div>
                      <div className="expand-indicator">
                        <FontAwesomeIcon icon={expandedItems.has(error.id) ? faChevronDown : faChevronRight} />
                      </div>
                    </div>
                    <div className="error-message">
                      <code>{error.errorMessage}</code>
                    </div>
                  </div>

                  {expandedItems.has(error.id) && (
                    <div className="error-details">
                      <div className="explanation-section">
                        <h4>
                          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem' }} />
                          What This Means
                        </h4>
                        <p>{error.explanation}</p>
                      </div>

                      <div className="solution-section">
                        <h4>
                          <FontAwesomeIcon icon={faHammer} style={{ marginRight: '0.5rem' }} />
                          Recommended Fix
                        </h4>
                        <p>{error.solution}</p>
                      </div>

                      {error.codeExample && (
                        <div className="code-section">
                          <h4>
                            <FontAwesomeIcon icon={faCode} style={{ marginRight: '0.5rem' }} />
                            Code Example
                          </h4>
                          <pre className="code-snippet">{error.codeExample}</pre>
                        </div>
                      )}

                      <div className="keywords-section">
                        <h4>
                          <FontAwesomeIcon icon={faTags} style={{ marginRight: '0.5rem' }} />
                          Related Keywords
                        </h4>
                        <div className="keywords-list">
                          {error.keywords.map((keyword, index) => (
                            <span key={index} className="keyword-tag">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {filteredErrors.length === 0 && searchTerm && (
            <div className="no-results">
              <div className="no-results-content">
                <FontAwesomeIcon icon={faSearch} className="no-results-icon" />
                <h3>No matching errors found</h3>
                <p>Try searching with different keywords or error message fragments.</p>
                <div className="search-suggestions">
                  <p>Common search terms:</p>
                  <div className="suggestion-tags">
                    {['GDAL', 'projection', 'memory', 'CRS', 'NetCDF', 'PostGIS'].map(term => (
                      <button
                        key={term}
                        className="suggestion-tag"
                        onClick={() => setSearchTerm(term)}
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DebuggingAssistant;